import { MatchingViewModel } from '../ViewModels';
import { MatchingHeader } from '../matching-config';
import { useMatchingEnricher } from './useMatchingEnricher';
import {
    ActionStatusHelper,
    DomainTableConfig,
    useAppEnum,
    ActionResponse,
    useLayoutLifeCycle,
} from '@/Shared';
import { DOMAIN_CONFIGS, DomainHookProps } from '@/Shared/Config/domain-config';
import { useMatching } from '@/OneLiners';
import { MatchingModel } from '../MatchingModel';


/**
 * useMatchingTableConfig hook
 *
 * Domain-specific layout composition hook for Matching entities.
 * Orchestrates domain data, enrichment, permissions, and UI table configuration.
 *
 * Responsibilities:
 * - Aggregate Matching domain state, enrichment, and permissions into a single response.
 * - Provide dynamic headers for Matching table (sector & city dependent).
 * - Map strict Model → ViewModel for UI consumption.
 * - Delegate lifecycle orchestration to useLayoutLifeCycle.
 *
 * @date: March 2, 2026
 * @author: Samano CASTRE
 * 
 * @see DomainHookProps
 * @see DomainTableConfig<MatchingViewModel>
 *
 * @param props Domain-level configuration and permissions
 * @returns DomainTableConfig<MatchingViewModel>
 */
export function useMatchingTableConfig(props: DomainHookProps): DomainTableConfig<MatchingViewModel> {
    const { permissionHook, accessRight } = props;
    const { domainType } = DOMAIN_CONFIGS.Matching;

    const { MatchingEnums } = useAppEnum();

    /**
     * Matching domain CRUD and state
     */
    const matchingHook = useMatching();

    /**
     * Enrichment layer
     * Resolves derived fields and cross-domain data
     */
    const { sectorMap, cityMap, getStrictEnrichedMatching, enricherResponse, } = useMatchingEnricher();

    /**
     * Combined readiness response
     * Only builds table once all sources succeeded
     */
    const response: ActionResponse = {
        status: ActionStatusHelper.combine(
            matchingHook.stateResponse.status,
            enricherResponse.status,
            permissionHook.stateResponse.status
        ),
        error: matchingHook.stateResponse.error ??
            enricherResponse.error
    };

    /**
     * Delegation to generic layout lifecycle.
     */
    const lifeCycleHook = useLayoutLifeCycle({
        response,
        domainType,
        props,
        entityHook: matchingHook,

        /**
         * Header configuration for Property table.
         */
        headers: MatchingHeader.build(sectorMap, cityMap, MatchingEnums),

        /**
         * Strict Model → ViewModel transformation.
         */
        toViewModelFn: (matching: MatchingModel, isDisplaying: boolean) =>
            new MatchingViewModel({
                matching: getStrictEnrichedMatching(matching),
                viewEnums: MatchingEnums,
                accessRight,
                isDisplaying
            })
    });

    return lifeCycleHook;
}